import { IFlight, IGroundEvent, IFullFlight } from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { isFlight } from "../redux/reducers/FlightTimelineReducer";
import { IBaseTime } from "../redux/reducers/CrewScheduleReducer";
import { getLocalTimeMapAt } from "./TimelineLabels/BaseTimelineLabelsGL";
import { IFlightErrorFlight } from "../pages/FlightTimeline/gl/EventErrors/EventErrorsGL";

export const timeScales = {
  hour: 3600000,
  day: 86400000,
  week: 604800000,
  month: 2592000000,
  "3month": 7776000000,
  year: 31556952000,
};

export const indexOfClosest = (nums: number[], target: number): number => {
  let closest = Number.MAX_SAFE_INTEGER;
  let index = 0;
  nums.forEach((num, i) => {
    const dist = Math.abs(target - num);
    if (dist < closest) {
      index = i;
      closest = dist;
    }
  });
  return index;
};

export const binarySearchAircraftEvents = (
  flights: IFlight[] | IGroundEvent[],
  searchTime: number,
  start: number,
  end: number
): IFlight | IGroundEvent | null => {
  // Base Condtion
  if (start > end) return null;

  // Find the middle index
  const mid = Math.floor((start + end) / 2);
  const event = flights[mid];
  let left = isFlight(event) ? new Date(event.actualOff).getTime() : new Date(event.actualStart).getTime();
  let right = isFlight(event) ? new Date(event.actualOn).getTime() : new Date(event.actualEnd).getTime();

  // Compare mid with given key x
  if (left > right) {
    const temp = left;
    left = right;
    right = temp;
  }
  if (left <= searchTime && right > searchTime) return event;

  // If element at mid is greater than x,
  // search in the left half of mid
  if (right > searchTime) return binarySearchAircraftEvents(flights, searchTime, start, mid - 1);
  // If element at mid is smaller than x,
  // search in the right half of mid
  else return binarySearchAircraftEvents(flights, searchTime, mid + 1, end);
};

export interface IRGBA {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

export const hexToRgb = (hex: string): number[] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16) / 255.0, parseInt(result[2], 16) / 255.0, parseInt(result[3], 16) / 255.0]
    : [1.0, 1.0, 1.0];
};

export const hexToIRGBA = (hex: string): IRGBA => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        red: parseInt(result[1], 16) / 255.0,
        green: parseInt(result[2], 16) / 255.0,
        blue: parseInt(result[3], 16) / 255.0,
        alpha: 1.0,
      }
    : {
        red: 1.0,
        green: 1.0,
        blue: 1.0,
        alpha: 1.0,
      };
};

/**
 * Return either black or white fex value based on based contrast to supplied hex color intput.
 */
export const invertHexColor = (hex: string): string => {
  if (!hex) {
    return "#000000";
  }
  if (hex.startsWith("#")) {
    hex = hex.slice(1);
  }
  // convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    throw new Error("Invalid HEX color.");
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const value = r * 0.299 + g * 0.587 + b * 0.114;
  // console.log(`%c ${value}`, `color: #${hex}`);
  return value > 165 ? "#000000" : "#FFFFFF";
};

export interface ITickInterval {
  timeMillis: number;
  label: string;
  dayStr?: string;
  dayIndexes?: number;
  isHighlighted: boolean;
}

const oneDayAsMillis = 8.64e7;
const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getMonthYearStr = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = months[date.getUTCMonth()];
  return `${month} ${year}`;
};

export const getLocalTimeDayTickIntervals = (
  minMillis: number,
  maxMillis: number,
  approxTickIntervalMillis: number,
  baseTime: IBaseTime
): [ITickInterval[], ITickInterval[]] => {
  const tickIntervalDays = calculateTickIntervalDays(approxTickIntervalMillis);

  const timeMap = getLocalTimeMapAt(baseTime.timezoneId, minMillis - (minMillis % (tickIntervalDays * 8.64e7)));
  const newMoment = timeMap.localMoment.clone().startOf("day");

  const endTimeMillis = maxMillis + oneDayAsMillis * tickIntervalDays;
  let currentMonth = newMoment.month();

  const tickTimesMillis: ITickInterval[] = [];
  const monthTicksMillis: ITickInterval[] = [
    {
      timeMillis: minMillis,
      label: newMoment.format("MMM YYYY"),
      isHighlighted: false,
    },
  ];

  while (newMoment?.valueOf() <= endTimeMillis) {
    // ensure ticks always fall on the first day of the month
    const newMonth = newMoment.month();
    if (newMonth !== currentMonth) {
      newMoment.date(1);
      const timeMillis = newMoment?.valueOf();
      monthTicksMillis.push({
        timeMillis,
        label: newMoment.format("MMM YYYY"),
        isHighlighted: true,
      });
      currentMonth = newMonth;
    }

    // prevent display of ticks too close to the end of the month
    const maxDom = new Date(newMoment.year(), newMoment.month() + 1, 0).getDate(); // m.clone().endOf('month').date();
    const dom = newMoment.date();
    if (maxDom - dom + 1 >= tickIntervalDays / 2) {
      const daysStr = newMoment.date() < 10 ? `0${newMoment.date()}` : `${newMoment.date()}`;
      tickTimesMillis.push({
        timeMillis: newMoment?.valueOf(),
        label: daysStr,
        dayStr: days[newMoment.day()],
        isHighlighted: newMoment.date() === 1,
      });
    }

    newMoment.date(newMoment.date() + tickIntervalDays);
  }
  return [tickTimesMillis, monthTicksMillis];
};

export const getDayTickIntervals = (
  minMillis: number,
  maxMillis: number,
  approxTickIntervalMillis: number,
  baseTime: IBaseTime
): [ITickInterval[], ITickInterval[]] => {
  const tickIntervalDays = calculateTickIntervalDays(approxTickIntervalMillis);

  const m = new Date(minMillis);
  m.setUTCDate(1);
  m.setUTCHours(0);
  m.setUTCMinutes(0);
  m.setUTCSeconds(0);
  m.setUTCMilliseconds(0);
  const endTimeMillis = maxMillis + oneDayAsMillis * tickIntervalDays;
  let currentMonth = m.getUTCMonth();

  const tickTimesMillis: ITickInterval[] = [];
  const monthTicksMillis: ITickInterval[] = [
    {
      timeMillis: minMillis,
      label: getMonthYearStr(m),
      isHighlighted: false,
    },
  ];

  while (m?.valueOf() <= endTimeMillis) {
    // ensure ticks always fall on the first day of the month
    const newMonth = m.getUTCMonth();
    if (newMonth !== currentMonth) {
      m.setUTCDate(1);
      const timeMillis = m.getTime();
      monthTicksMillis.push({
        timeMillis,
        label: getMonthYearStr(m),
        isHighlighted: true,
      });
      currentMonth = newMonth;
    }

    // prevent display of ticks too close to the end of the month
    const maxDom = new Date(m.getUTCFullYear(), m.getUTCMonth() + 1, 0).getDate(); // m.clone().endOf('month').date();
    const dom = m.getUTCDate();
    if (maxDom - dom + 1 >= tickIntervalDays / 2) {
      const daysStr = m.getUTCDate() < 10 ? `0${m.getUTCDate()}` : `${m.getUTCDate()}`;
      tickTimesMillis.push({
        timeMillis: m.getTime(),
        label: daysStr,
        dayStr: days[m.getUTCDay()],
        isHighlighted: m.getUTCDate() === 1,
      });
    }

    m.setUTCDate(m.getUTCDate() + tickIntervalDays);
  }
  return [tickTimesMillis, monthTicksMillis];
};

export const getHourTickIntervals = (
  minMillis: number,
  maxMillis: number,
  approxTickIntervalMillis: number,
  baseTime: IBaseTime
): [ITickInterval[], ITickInterval[]] => {
  const tickIntervalMillis = calculateTickIntervalMillis(approxTickIntervalMillis);
  const ticksSince1970 = Math.floor(minMillis / tickIntervalMillis);
  const firstTickMillis = ticksSince1970 * tickIntervalMillis;

  let m = baseTime.isUTC
    ? moment(firstTickMillis).utc().startOf("day").valueOf()
    : moment(firstTickMillis).tz(baseTime.timezoneId).startOf("day").valueOf();
  //lets make sure we are starting at 00:00
  m -= tickIntervalMillis;
  let currentDay = -1;
  const dayTicksMillis: ITickInterval[] = [];
  const tickTimesMillis: ITickInterval[] = [];
  // Count occurrences of each dayStr and assign indexes
  const dayStrCounts: { [dayStr: string]: number } = {};
  while (m < maxMillis) {
    m += tickIntervalMillis;
    const currentDate = new Date(m);
    let hours = currentDate.getUTCHours();
    let minutes = currentDate.getUTCMinutes();
    let newDay = currentDate.getUTCDate();
    let month = months[currentDate.getUTCMonth()];
    let day = currentDate.getUTCDate();
    let dayOfWeek = days[currentDate.getUTCDay()];

    if (!baseTime.isUTC) {
      const timeMap = getLocalTimeMapAt(baseTime.timezoneId, m);

      hours = timeMap.hours;
      minutes = timeMap.minutes;
      newDay = timeMap.localMoment.date();
      month = months[timeMap.localMoment.month()];
      day = timeMap.localMoment.date();
      dayOfWeek = days[timeMap.localMoment.day()];
    }

    const isHighlighted = hours === 0 && minutes === 0;

    const hoursStr = hours < 10 ? `0${hours}` : `${hours}`;
    const minStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const dayStr = tickTimesMillis.find((tick) => tick.dayStr === dayOfWeek) ? undefined : `${dayOfWeek}`;
    if (newDay !== currentDay) {
      dayTicksMillis.push({
        label: `${dayOfWeek} ${month} ${day}`,
        timeMillis: m,
        isHighlighted,
        dayStr: dayStr,
      });
      currentDay = newDay;
    }

    tickTimesMillis.push({
      timeMillis: m?.valueOf(),
      label: `${hoursStr}:${minStr}`,
      isHighlighted,
      dayStr: dayStr,
    });
    dayStrCounts[`${dayOfWeek}`] = (dayStrCounts[`${dayOfWeek}`] || 0) + 1;
  }

  //count total occurrences of each dayStr so that we can know the full span in hours
  tickTimesMillis.forEach((tick) => {
    if (tick.dayStr) {
      tick.dayIndexes = dayStrCounts[tick.dayStr] || 0;
    }
  });

  return [tickTimesMillis, dayTicksMillis];
};

export const tickIntervalRungsDays = [1, 2, 3, 4, 5, 8, 10];

export const calculateTickIntervalDays = (tickIntervalMillis: number): number => {
  const tickIntervalDays = moment.duration(tickIntervalMillis, "milliseconds").asDays();

  const tickInterval = tickIntervalRungsDays.find((intervalDays: number) => {
    return Math.floor(tickIntervalDays) <= intervalDays;
  });

  return tickInterval ? tickInterval : tickIntervalRungsDays[tickIntervalRungsDays.length - 1];
};

export const tickIntervalRungsMillis = [
  moment.duration(1, "seconds").asMilliseconds(),
  moment.duration(2, "seconds").asMilliseconds(),
  moment.duration(5, "seconds").asMilliseconds(),
  moment.duration(10, "seconds").asMilliseconds(),
  moment.duration(15, "seconds").asMilliseconds(),
  moment.duration(20, "seconds").asMilliseconds(),
  moment.duration(30, "seconds").asMilliseconds(),
  moment.duration(1, "minutes").asMilliseconds(),
  moment.duration(2, "minutes").asMilliseconds(),
  moment.duration(5, "minutes").asMilliseconds(),
  moment.duration(10, "minutes").asMilliseconds(),
  moment.duration(15, "minutes").asMilliseconds(),
  moment.duration(20, "minutes").asMilliseconds(),
  moment.duration(30, "minutes").asMilliseconds(),
  moment.duration(1, "hours").asMilliseconds(),
  moment.duration(2, "hours").asMilliseconds(),
  moment.duration(3, "hours").asMilliseconds(),
  moment.duration(6, "hours").asMilliseconds(),
  moment.duration(12, "hours").asMilliseconds(),
  moment.duration(1, "days").asMilliseconds(),
  moment.duration(1, "days").asMilliseconds(),
];

function calculateTickIntervalMillis(approxTickIntervalMillis: number): number {
  for (let n = 0; n < tickIntervalRungsMillis.length; n++) {
    if (approxTickIntervalMillis <= tickIntervalRungsMillis[n]) {
      return tickIntervalRungsMillis[n];
    }
  }
  return moment.duration(1, "days").asMilliseconds();
}

export const differenceInDays = (firstDate: number, secondDate: number): number => {
  return Math.ceil((secondDate - firstDate) / (1000 * 60 * 60 * 24));
};

export const setBeginningDay = (dateMillis: number): Date => {
  const date = new Date(dateMillis);
  date.setUTCHours(0);
  date.setUTCMinutes(0);
  return date;
};

/* Finding closest flight index */

/**
 * getClosestIndex helper function to find which index is closer to the target
 * @param flightsthe - array of flights to search for closest flight
 * @param firstIndex - the first index to compare if it's closer to target
 * @param secondIndex - the second index to compare against to see which is closer
 * @param target - the target time (in millis) to search for
 * @param comparePropthe - property to actaully compare. Either departure or arrival
 * @returns number Either firstIndex or secondIndex; whichever is closer to the target
 */
const getClosestIndex = (
  flights: IFlight[] | IGroundEvent[] | IFlightErrorFlight[],
  firstIndex: number,
  secondIndex: number,
  target: number,
  compareProp: EventTimes
): number => {
  ///@ts-ignore  requires rewrite of the timeline data.
  const val1 = new Date(flights[firstIndex][compareProp]).getTime();
  ///@ts-ignore  requires rewrite of the timeline data.
  const val2 = new Date(flights[secondIndex][compareProp]).getTime();
  return target - val1 >= val2 - target ? secondIndex : firstIndex;
};

/**
 * Find the closest index for either a flight or ground event
 * @param events
 * @param firstIndex
 * @param secondIndex
 * @param target
 * @param flightCompareProp
 * @param groundEventCompareProp
 * @returns
 */
const getClosestTimelineEventIndex = (
  events: (IFlight | IGroundEvent | IFullFlight)[],
  firstIndex: number,
  secondIndex: number,
  target: number,
  flightCompareProp: EventTimes,
  groundEventCompareProp: EventTimes
): number => {
  const val1 = isFlight(events[firstIndex])
    ? ///@ts-ignore  requires rewrite of the timeline data.
      new Date(events[firstIndex][flightCompareProp]).getTime()
    : ///@ts-ignore  requires rewrite of the timeline data.
      new Date(events[firstIndex][groundEventCompareProp]).getTime();
  const val2 = isFlight(events[secondIndex])
    ? ///@ts-ignore  requires rewrite of the timeline data.
      new Date(events[secondIndex][flightCompareProp]).getTime()
    : ///@ts-ignore  requires rewrite of the timeline data.
      new Date(events[secondIndex][groundEventCompareProp]).getTime();
  return target - val1 >= val2 - target ? secondIndex : firstIndex;
};

export type EventTimes = "actualOff" | "actualOn" | "actualStart" | "actualEnd";
/**
 * findClosestFlightIndex finds the closest flight with scheduledDeparture close to the target time
 * @param flights - the array of flights to search for closest flight
 * @param target - the target time (in millis) to search for
 * @param compareProp - the property to actaully compare. Either departure or arrival
 * @returns index returns the index of the flight that has closest time to the target
 */
export const findClosestFlightIndex = (
  flights: IFlight[] | IGroundEvent[] | IFlightErrorFlight[],
  target: number,
  compareProp: EventTimes
): number => {
  const length = flights.length;
  if (length === 0) return -1;

  // Corner cases
  ///@ts-ignore  requires rewrite of the timeline data.
  if (target <= new Date(flights[0][compareProp]).getTime()) return 0;
  ///@ts-ignore  requires rewrite of the timeline data.
  if (target >= new Date(flights[length - 1][compareProp]).getTime()) return length - 1;

  // Doing binary search
  let i = 0,
    j = length,
    mid = 0;
  while (i < j) {
    mid = Math.floor((i + j) / 2);
    ///@ts-ignore  requires rewrite of the timeline data.
    const midTime = new Date(flights[mid][compareProp]).getTime();
    if (target === midTime) return mid;

    /* If target is less than array element, 
        then search in left */
    if (target < midTime) {
      // If target is greater than previous
      // to mid, return closest of two
      ///@ts-ignore  requires rewrite of the timeline data.
      const previousTime = new Date(flights[mid - 1][compareProp]).getTime();
      if (mid > 0 && target > previousTime) return getClosestIndex(flights, mid - 1, mid, target, compareProp);

      /* Repeat for left half */
      j = mid;
    }
    // If target is greater than mid
    else {
      ///@ts-ignore  requires rewrite of the timeline data.
      const nextTime = new Date(flights[mid + 1][compareProp]).getTime();
      if (mid < length - 1 && target < nextTime) {
        return getClosestIndex(flights, mid, mid + 1, target, compareProp);
      }
      i = mid + 1; // update i
    }
  }

  // Only single element left after search
  return mid;
};

/**
 * Find the closest event for either a flight or ground event
 * @param events
 * @param target
 * @param flightCompareProp
 * @param groundEventCompareProp
 * @returns
 */
export const findClosestFlightTimelineEventIndex = (
  events: (IFlight | IGroundEvent | IFullFlight)[],
  target: number,
  flightCompareProp: EventTimes,
  groundEventCompareProp: EventTimes
): number => {
  const length = events.length;
  if (length === 0) return -1;

  // Corner cases
  if (
    target <=
    (isFlight(events[0])
      ? ///@ts-ignore  requires rewrite of the timeline data.
        new Date(events[0][flightCompareProp]).getTime()
      : ///@ts-ignore  requires rewrite of the timeline data.
        new Date(events[0][groundEventCompareProp]).getTime())
  )
    return 0;
  if (
    target >=
    (isFlight(events[length - 1])
      ? ///@ts-ignore  requires rewrite of the timeline data.
        new Date(events[length - 1][flightCompareProp]).getTime()
      : ///@ts-ignore  requires rewrite of the timeline data.
        new Date(events[length - 1][groundEventCompareProp]).getTime())
  )
    return length - 1;

  // Doing binary search
  let i = 0,
    j = length,
    mid = 0;
  while (i < j) {
    mid = Math.floor((i + j) / 2);

    const midTime = isFlight(events[mid])
      ? ///@ts-ignore  requires rewrite of the timeline data.
        new Date(events[mid][flightCompareProp]).getTime()
      : ///@ts-ignore  requires rewrite of the timeline data.
        new Date(events[mid][groundEventCompareProp]).getTime();
    if (target === midTime) return mid;

    /* If target is less than array element, 
        then search in left */
    if (target < midTime) {
      // If target is greater than previous
      // to mid, return closest of two
      const previousTime = isFlight(events[mid - 1])
        ? ///@ts-ignore  requires rewrite of the timeline data.
          new Date(events[mid - 1][flightCompareProp]).getTime()
        : ///@ts-ignore  requires rewrite of the timeline data.
          new Date(events[mid - 1][groundEventCompareProp]).getTime();
      if (mid > 0 && target > previousTime)
        return getClosestTimelineEventIndex(events, mid - 1, mid, target, flightCompareProp, groundEventCompareProp);

      /* Repeat for left half */
      j = mid;
    }
    // If target is greater than mid
    else {
      const nextTime = isFlight(events[mid + 1])
        ? ///@ts-ignore  requires rewrite of the timeline data.
          new Date(events[mid + 1][flightCompareProp]).getTime()
        : ///@ts-ignore  requires rewrite of the timeline data.
          new Date(events[mid + 1][groundEventCompareProp]).getTime();
      if (mid < length - 1 && target < nextTime) {
        return getClosestTimelineEventIndex(events, mid, mid + 1, target, flightCompareProp, groundEventCompareProp);
      }
      i = mid + 1; // update i
    }
  }

  // Only single element left after search
  return mid;
};
